import React, { useMemo, useRef, useState, useEffect } from 'react';
import { filter, some, forEach, groupBy, isEmpty } from "lodash";
import { Button, DateField, Divider, MenuItem, SelectField, Stack } from '@curves/ui-base';

import { useChartContext } from "@Local/Dashboard/Components/ChartContext";
import { useSnackbar } from "notistack";
import { getDiffDate, getFirstOfMonth, getLastOfMonth, getSubMonths, isBefore } from "@Util/moment.util";
import { dataTypeMap, filterAxisTypeMap, filterRangeTypeMap, filterTargetTypeMap } from "@Local/Dashboard/staticData";
import useChartAdvancedFilterApi from "@Apis/useChartAdvancedFilterApi";
const ChartAdvancedStatisticsResultBox = React.forwardRef((props, ref) => {
    const { enqueueSnackbar: _snackbar } = useSnackbar();
    const { selectedFilter, selectedStatistics, setSourceData } = useChartContext();
    const { postChartAdvFilterStatisticsApi } = useChartAdvancedFilterApi();
    const {
        dataType: dataTypeProps,
        chartType: chartTypeProps,
        options: optionsProps
    } = props;
    const assignDate = getSubMonths(new Date(), 1);
    const [mainOptions, setMainOptions] = useState([]);
    const [hasClubAxis, setHasClubAxis] = useState(false);
    const mainSelectRef = useRef(null);
    const [rangeStart] = useState(getFirstOfMonth(assignDate));
    const [rangeEnd] = useState(getLastOfMonth(assignDate));
    const [selectedMainAxis, setSelectedMainAxis] = useState(null);
    const cateRangeStartRef = useRef(null);
    const cateRangeEndRef = useRef(null);
    const handleChangeMainTarget = (e) => {
        if (!e.target.value) return;
        const { value: tagType } = e.target.value || {}
        if (!tagType) return;
        setSelectedMainAxis(e.target.value);
    };
    const handleChangeDateRange = (rangeType) => {
        if (rangeType === filterRangeTypeMap.monthly) {
            cateRangeStartRef.current.resetValue(getFirstOfMonth(assignDate));
            cateRangeEndRef.current.resetValue(getLastOfMonth(assignDate));
        } else if (rangeType === filterRangeTypeMap.quarterly) {
            cateRangeStartRef.current.resetValue(getFirstOfMonth(getSubMonths(assignDate, 2)));
            cateRangeEndRef.current.resetValue(getLastOfMonth(assignDate));
        } else if (rangeType === filterRangeTypeMap.yearly) {
            cateRangeStartRef.current.resetValue(getFirstOfMonth(getSubMonths(assignDate, 12)));
            cateRangeEndRef.current.resetValue(getLastOfMonth(assignDate));
        }
    };
    const handleChartDisplay = async () => {
        const params = {
            dataType: dataTypeProps,
            chartType: chartTypeProps,
            filterItems: selectedFilter,
            statisticsItems: selectedStatistics,
            cateTarget: mainSelectRef.current.getResult(),
            cateStart: null,
            cateEnd: null
        };
        if (isEmpty(params.cateTarget)) {
            _snackbar("請選擇查詢數值", { variant: "info" });
            return;
        }
        params.cateStart = cateRangeStartRef.current.getResult();
        params.cateEnd = cateRangeEndRef.current.getResult();
        if (!isBefore(params.cateStart, params.cateEnd)) {
            _snackbar("查詢結束時間不可小於等於起始時間", { variant: "info" });
            return;
        } else if (getDiffDate(params.cateStart, params.cateEnd, 'year') <= -2) {
            _snackbar("查詢時間不可超過兩年", { variant: "info" });
            return;
        }
        const resp = await postChartAdvFilterStatisticsApi(params);
        if (resp) {
            setSourceData(resp.data);
            // console.log(params);
            // console.log(JSON.stringify(resp.data));
        }
    };
    useEffect(() => {
            const {
                clubTagGroupOptions = []
            } = optionsProps || {};
            const selectTags = filter(selectedFilter, (item) => item.targetType !== filterTargetTypeMap.singleClub);
            const tagTypes = Object.keys(groupBy(selectTags, 'tagType')).map(Number);
            const mainOptions = [];
            let isClubAxis = some(selectedFilter, { targetType: filterTargetTypeMap.singleClub });
            forEach(clubTagGroupOptions, item => {
                const { tagType, tagTypeText } = item;
                const axisType = filterAxisTypeMap.clubTagType;
                if (tagTypes.indexOf(item.tagType) >= 0) {
                    mainOptions.push({ text: tagTypeText, value: tagType, axisType });
                    isClubAxis = true;
                }
            });
            setMainOptions(mainOptions);
            setHasClubAxis(isClubAxis);
            if (mainSelectRef) mainSelectRef.current.value = "";
        },
        // eslint-disable-next-line
        [selectedFilter, selectedStatistics, optionsProps]);
    const MainSelectField = useMemo(
        () =>
            <SelectField ref={mainSelectRef} label={'分類'} defaultValue={selectedMainAxis} onChange={handleChangeMainTarget} fullWidth>
                <MenuItem value="" disabled>
                    <em>請選擇</em>
                </MenuItem>
                {hasClubAxis && dataTypeProps !== dataTypeMap.statisticsCoach && (
                    <MenuItem value={{ axisType: filterAxisTypeMap.club }}>
                        <em>店舖</em>
                    </MenuItem>
                )}
                {mainOptions.length > 0 && dataTypeProps !== dataTypeMap.statisticsCoach && mainOptions.map((option, index) => (
                    <MenuItem key={index}
                              value={{ axisType: option.axisType, value: option.value }}>
                        <em>{option.text}</em>
                    </MenuItem>
                ))}
                {dataTypeProps === dataTypeMap.statisticsCoach && (
                    <MenuItem value={{ axisType: filterAxisTypeMap.coach }}>
                        <em>教練</em>
                    </MenuItem>
                )}
            </SelectField>
        ,
        // eslint-disable-next-line
        [mainOptions]
    );
    return (
        <React.Fragment>
            <Stack direction="column" spacing={1}>
                {MainSelectField}
                <label className="formlabel"><span>時間</span></label>
                <Stack direction="row" spacing={1}>
                    <DateField ref={cateRangeStartRef} placeholder="起始時間" defaultValue={rangeStart} />
                    <DateField ref={cateRangeEndRef} placeholder="結束時間" defaultValue={rangeEnd} />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" onClick={() => handleChangeDateRange(filterRangeTypeMap.monthly)} color="primary">月</Button>
                    <Button variant="outlined" size="small" onClick={() => handleChangeDateRange(filterRangeTypeMap.quarterly)} color="primary">季</Button>
                    <Button variant="outlined" size="small" onClick={() => handleChangeDateRange(filterRangeTypeMap.yearly)} color="primary">年</Button>
                </Stack>
            </Stack>
            <Divider className="my-2" />
            <Stack direction="row" spacing={1}>
                <Button variant="contained" color="primary" onClick={handleChartDisplay}>產生圖表</Button>
            </Stack>
        </React.Fragment>
    );
});
export default ChartAdvancedStatisticsResultBox;